import React from "react";

export type StatsCardAccent = "green" | "red" | "primary";

interface StatsCardProps {
  label: string;
  value: string | number;
  accent?: StatsCardAccent;
  icon?: React.ReactNode;
  subtitle?: string;
  delta?: string;
  loading?: boolean;
}

const accentConfig: Record<
  StatsCardAccent,
  {
    glow: string;
    iconBg: string;
    iconColor: string;
    deltaColor: string;
    borderGlow: string;
  }
> = {
  green: {
    glow: "linear-gradient(180deg, rgb(var(--success) / 0.16) 0%, transparent 75%)",
    iconBg: "rgb(var(--success) / 0.12)",
    iconColor: "rgb(var(--success))",
    deltaColor: "rgb(var(--success))",
    borderGlow: "rgb(var(--success) / 0.18)",
  },

  red: {
    glow: "linear-gradient(180deg, rgb(var(--danger) / 0.16) 0%, transparent 75%)",
    iconBg: "rgb(var(--danger) / 0.12)",
    iconColor: "rgb(var(--danger))",
    deltaColor: "rgb(var(--danger))",
    borderGlow: "rgb(var(--danger) / 0.18)",
  },

  primary: {
    glow: "linear-gradient(180deg, rgb(var(--color-gold-2) / 0.18) 0%, transparent 75%)",
    iconBg: "rgb(var(--color-gold-2) / 0.12)",
    iconColor: "rgb(var(--color-gold-2))",
    deltaColor: "rgb(var(--color-gold-2))",
    borderGlow: "rgb(var(--color-gold-2) / 0.18)",
  },
};

export default function StatsCard({
  label,
  value,
  accent = "primary",
  icon,
  subtitle,
  delta,
  loading = false,
}: StatsCardProps) {
  const config = accentConfig[accent];

  return (
    <div
      className="
        group
        relative
        overflow-hidden

        rounded-md
        border

        min-h-[132px]
        p-4

        bg-[rgb(var(--surface-1))]
        border-[rgb(var(--border-primary))]

        transition-all
        duration-300
        ease-out

        hover:-translate-y-[2px]
        hover:border-[rgb(var(--border-secondary))]
      "
    >
      {/* Glow Background */}
      <div
        className="
          absolute
          inset-0
          opacity-80
          pointer-events-none
        "
        style={{
          background: config.glow,
        }}
      />

      {/* Hover Glow */}
      <div
        className="
          absolute
          -top-24
          right-[-40px]

          h-40
          w-40
          rounded-md
          blur-3xl

          opacity-0
          group-hover:opacity-100

          transition-all
          duration-500
          pointer-events-none
        "
        style={{
          background: config.borderGlow,
        }}
      />

      {/* Subtle Border Highlight */}
      <div
        className="
          absolute
          inset-0
          rounded-md
          opacity-0
          group-hover:opacity-100
          transition-opacity
          duration-300
          pointer-events-none
        "
        style={{
          boxShadow: `inset 0 1px 0 ${config.borderGlow}`,
        }}
      />

      {/* Content */}
      <div className="relative z-[1] flex flex-col h-full">
        {/* Top */}
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <span
              className="
                text-[10px]
                uppercase
                tracking-[0.16em]
                font-semibold
                text-[rgb(var(--text-secondary))]
              "
            >
              {label}
            </span>

            {subtitle && (
              <span
                className="
                  text-[11px]
                  leading-relaxed
                  text-[rgb(var(--text-tertiary))]
                "
              >
                {subtitle}
              </span>
            )}
          </div>

          {icon && (
            <div>
              <div
                className="
                relative
                flex
                items-center
                justify-center

                w-11
                h-11
                rounded-xl

                border

                transition-all
                duration-300

                group-hover:scale-105
                group-hover:-translate-y-[1px]
              "
                style={{
                  background: config.iconBg,
                  color: config.iconColor,
                  borderColor: "rgb(var(--border-primary))",
                }}
              >
                {/* Icon Glow */}
                <div
                  className="
                  absolute
                  inset-0
                  rounded-xl
                  opacity-0
                  group-hover:opacity-100
                  blur-xl
                  transition-opacity
                  duration-300
                "
                  style={{
                    background: config.borderGlow,
                  }}
                />
                <div className="relative z-[1]">{icon}</div>
              </div>
            </div>
          )}
        </div>

        {/* Metric */}
        <div className="flex items-end justify-between gap-3">
          {loading ? (
            <div className="space-y-2">
              <div className="h-9 w-24 rounded-lg bg-[rgb(var(--surface-3))] animate-pulse" />
              <div className="h-3 w-16 rounded bg-[rgb(var(--surface-3))] animate-pulse" />
            </div>
          ) : (
            <>
              <div className="flex flex-col">
                <span
                  className="
                    text-[34px]
                    leading-none
                    font-[300]
                    tracking-[-0.03em]
                    text-[rgb(var(--text-primary))]
                  "
                >
                  {typeof value === "number" ? value.toLocaleString() : value}
                </span>

                {delta && (
                  <span
                    className="
                      mt-2
                      text-[11px]
                      font-medium
                    "
                    style={{
                      color: config.deltaColor,
                    }}
                  >
                    {delta}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
